import { useEffect, useState } from 'react';

import { keyframes } from '@emotion/css';
import styled from '@emotion/styled';
import {
  Colors,
  Button,
  KeyboardTabLine,
  SyncLine,
  HomeLine,
  BankBookLine,
  CorporateLine,
  MonitoringLine,
  MoreHorizLine,
  BellLine,
  QuestionCircleLine,
  DatabaseLine,
  Flex,
  LinkLine,
} from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { CUSTOM_ATTRIBUTES } from '@revenuemarket/utils';
import { Divider } from 'antd';

import { CompanyUserPermission } from '~apis/role/type';
import { postIntegratedScraping } from '~apis/scrape';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import checkIsOnboardingUser from '~hooks/checkIsOnboardingUser';
import { useGetSettings } from '~hooks/queries/setting/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import NavigationBody from './NavigationBody';
import NavigationButton from './NavigationButton';
import useNavigationStore from './useNavigationStore';

export const NAVIGATION_SIDEBAR_WIDTH = 240;
export const COLLAPSED_NAVIGATION_SIDEBAR_WIDTH = 56;

const NavigationWrapper = styled(Flex)`
  width: ${COLLAPSED_NAVIGATION_SIDEBAR_WIDTH}px;
  height: 100vh;
  cursor: pointer;
  background-color: ${Colors.gray50};
  border-right: 1px solid ${Colors.grayOpacity07};
`;

const slide = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideReverse = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const FloatingWrapper = styled.div<{ floatEnabled: boolean }>`
  position: absolute;
  left: 8px;
  top: 8px;
  width: ${NAVIGATION_SIDEBAR_WIDTH}px;
  height: calc(100vh - 16px);
  background-color: transparent;
  z-index: 100000;
  opacity: ${(props) => (props.floatEnabled ? 1 : 0)};
  pointer-events: ${(props) => (props.floatEnabled ? 'auto' : 'none')};
  transition:
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
  animation: ${(props) => (props.floatEnabled ? slide : slideReverse)} 0.3s ease-in-out;
  visibility: ${(props) => (props.floatEnabled ? 'visible' : 'hidden')};
`;

const FloatingInner = styled.div`
  box-shadow:
    0px 4px 24px 0px ${Colors.grayOpacity12},
    rgba(29, 34, 37, 0.12);
  border-radius: 12px;
  border: 1px solid ${Colors.grayOpacity07};
  width: ${NAVIGATION_SIDEBAR_WIDTH}px;
  height: calc(100vh - 16px);
  background-color: ${Colors.gray50};
`;

interface NavigationProps {
  handlePannelPinClick: () => void;
  isPartnerIntro: boolean;
}
const SideBarPanel: React.FC<NavigationProps> = ({ handlePannelPinClick, isPartnerIntro }) => {
  return (
    <Flex
      direction="column"
      style={{
        width: NAVIGATION_SIDEBAR_WIDTH,
        minWidth: NAVIGATION_SIDEBAR_WIDTH,
        height: '100vh',
        backgroundColor: Colors.gray50,
        borderRight: `1px solid ${Colors.grayOpacity07}`,
      }}
    >
      <NavigationBody
        onPannelPinClick={() => handlePannelPinClick()}
        isPartnerIntro={isPartnerIntro}
      />
    </Flex>
  );
};

const Navigation = ({ isPartnerIntro }: { isPartnerIntro: boolean }) => {
  const { companyId } = useCompanyId();
  const settings = useGetSettings();
  const settingData = settings?.data?.Data?.data;
  const step = settingData?.onboardingStep;
  const isOnboardingUser = checkIsOnboardingUser(step);
  const { pinned, setPinned } = useNavigationStore();

  const [isFloating, setIsFloating] = useState(false);

  const [enterTimer, setEnterTimer] = useState<NodeJS.Timeout | null>(null);
  const [leaveTimer, setLeaveTimer] = useState<NodeJS.Timeout | null>(null);

  const onScrape = async () => {
    if (!companyId) {
      return;
    }

    mixpanel.track(MIXPANEL_EVENT.SCRAPE.DEFAULT);
    const response = await postIntegratedScraping(companyId as string);

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('수집 요청이 완료되었습니다.');
    } else {
      Toast.error('수집 요청에 실패했습니다.');
    }
  };

  useEffect(() => {
    const pinned = window.localStorage.getItem('clobe-nav-pinned');
    if (pinned) {
      setPinned(JSON.parse(pinned) as boolean);
    }
  }, []);

  const handleMouseLeave = () => {
    if (enterTimer) {
      clearTimeout(enterTimer); // 마우스를 빠르게 이동하면 enter 타이머 제거
      setEnterTimer(null);
    }

    const timer = setTimeout(() => {
      setIsFloating(false);
    }, 300);
    setLeaveTimer(timer);
  };

  if (!pinned) {
    return (
      <div
        style={{
          position: 'relative',
          height: '100vh',
        }}
      >
        {' '}
        <NavigationWrapper direction="column" align="center" justify="space-between">
          <Flex direction="column" align="center" gap={12}>
            <Button
              icon={<KeyboardTabLine />}
              buttonType="transparent"
              style={{
                width: 32,
                marginTop: 12,
              }}
              onClick={() => {
                setPinned(!pinned);
              }}
              {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.PIN_NAVIGATION}
            />

            <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_DATA_INTEGRATION}>
              <NavigationButton
                icon={<SyncLine />}
                text="재수집"
                buttonType="transparent"
                onClick={onScrape}
                {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.DATA_REFRESH}
              />
            </PermissionGuard>
            <Divider
              style={{
                margin: '0',
              }}
            />

            <NavigationButton
              icon={<HomeLine />}
              text="홈"
              buttonType="transparent"
              href="/clobe/home"
              {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.HOME}
            />
            <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_ACCOUNTS}>
              <NavigationButton
                icon={<BankBookLine />}
                text="계좌"
                buttonType="transparent"
                href="/clobe/cash-status"
                {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.ACCOUNTS}
              />
            </PermissionGuard>
            <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_BUSINESS_ENTITIES}>
              <NavigationButton
                icon={<CorporateLine />}
                text="거래처"
                buttonType="transparent"
                href="/clobe/sales-purchase"
                {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.PARTNERS}
              />
            </PermissionGuard>
            <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_ANALYSIS}>
              <NavigationButton
                icon={<MonitoringLine />}
                text="손익"
                buttonType="transparent"
                href="/clobe/cash-flow"
                {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.PROFIT_LOSS}
              />
            </PermissionGuard>
            <NavigationButton
              icon={<MoreHorizLine />}
              text="더보기"
              buttonType="transparent"
              onClick={() => {
                setIsFloating(true);
              }}
              {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.MORE_MENU}
            />
          </Flex>
          <Flex
            direction="column"
            align="center"
            gap={12}
            style={{
              marginBottom: 18,
            }}
          >
            <Divider
              style={{
                margin: '0',
              }}
            />
            <NavigationButton
              icon={<BellLine />}
              text="알림"
              buttonType="transparent"
              href="/clobe/notifications"
              {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.NOTIFICATIONS}
            />

            <NavigationButton
              icon={<DatabaseLine />}
              text="연동"
              buttonType="transparent"
              href="/clobe/data-manage"
              {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.INTEGRATIONS}
            />

            <NavigationButton
              icon={<LinkLine />}
              text="이용 가이드"
              buttonType="transparent"
              href="https://docs.clobe.ai/"
            />

            {!isPartnerIntro && (
              <NavigationButton
                icon={<QuestionCircleLine />}
                text="문의"
                buttonType="transparent"
                {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.SUPPORT_INQUIRY}
                onClick={() => {
                  window.Intercom('showSpace', 'home');
                }}
              />
            )}
          </Flex>
        </NavigationWrapper>
        <FloatingWrapper
          floatEnabled={isFloating}
          onMouseLeave={handleMouseLeave}
          {...CUSTOM_ATTRIBUTES.NAVIGATION_INDEX.FLOATING_NAVIGATION}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              width: NAVIGATION_SIDEBAR_WIDTH,
            }}
          >
            <FloatingInner>
              {' '}
              <NavigationBody
                onPannelPinClick={() => {
                  setIsFloating(false);
                  setPinned(!pinned);
                }}
                onCloseClick={() => {
                  setIsFloating(false);
                }}
                floating
                isPartnerIntro={isPartnerIntro}
              />
            </FloatingInner>
          </div>
        </FloatingWrapper>
      </div>
    );
  }

  return (
    <SideBarPanel
      handlePannelPinClick={() => {
        setPinned(!pinned);
      }}
      isPartnerIntro={isPartnerIntro}
    />
  );
};

export default Navigation;
